/**
 * TopBar - Sticky top navigation bar for both desktop and mobile viewports.
 *
 * Replaces the previous NavigationBar as the horizontal bar at the top of the
 * app. Works alongside the Sidebar (desktop) and BottomTabBar (mobile) to
 * form the complete navigation shell.
 *
 * Desktop layout (left to right):
 *	 1. Search input (rounded, gray background, search icon, placeholder text)
 *	 2. Flexible spacer
 *	 3. Notification bell icon (with optional red unread dot)
 *	 4. Sync status icon (rotating arrows with green/red dot for connectivity)
 *	 5. User name + role label (stacked vertically) + avatar circle (first initial)
 *	 6. Avatar dropdown menu: "My Profile" and "Sign out"
 *
 * Mobile layout (left to right):
 *	 1. "GBRANDS" brand text in heading font
 *	 2. Flexible spacer
 *	 3. Sync status icon with connectivity dot
 *	 4. Notification bell icon
 *	 5. Avatar circle (compact, no name/role text)
 *	 (Search is omitted on mobile — available on the dashboard page instead)
 *
 * Styling:
 *	 - Sticky at top of the viewport
 *	 - White background with bottom border and subtle shadow
 *	 - 64px height
 *	 - On desktop, offset left by 240px to account for the Sidebar width
 *
 * Hooks:
 *	 - useAuth() for user name, role, and logout
 *	 - useOfflineSync() for online/offline status (sync icon dot color)
 */
import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../hooks/useAuth';
import { useOfflineSync } from '../../../hooks/useOfflineSync';
/* Services for the global search dropdown — query both endpoints in parallel
   as the user types and merge the results into a single dropdown panel. */
import { workOrderService, type WorkOrder } from '../../../services/workOrderService';
import { inspectionService, type Inspection } from '../../../services/inspectionService';
/* Quick "Add New User" popup — surfaced here so managers/admins can onboard a
   tech or individual from anywhere in the app. */
import { AddUserModal } from '../AddUserModal';
import { BrandWordmark } from '../../atoms';
import { USER_CREATE_ROLES } from '../../../utils/constants';

/**
 * Props for the TopBar component.
 *
 * @property className - Optional outer className merged onto the root element
 */
export interface TopBarProps {
	className?: string;
}

const TopBar: React.FC<TopBarProps> = ({ className }) => {
	/* ---------------------------------------------------------------
	 * Hooks
	 * --------------------------------------------------------------- */

	/** Auth context — provides user info, role, role checks, and logout function */
	const { user, logout, hasAnyRole } = useAuth();

	/** Whether the current user can quick-add other users (managers and up). */
	const canAddUsers = hasAnyRole([...USER_CREATE_ROLES]);

	/** Controls visibility of the quick "Add New User" modal. */
	const [addUserOpen, setAddUserOpen] = useState(false);

	/** Offline context — provides isOnline flag for sync icon dot color */
	const { isOnline } = useOfflineSync();

	/** Programmatic navigation for client-side routing */
	const navigate = useNavigate();

	/* ---------------------------------------------------------------
	 * Local UI state
	 * --------------------------------------------------------------- */

	/** Controls visibility of the avatar dropdown menu (desktop only) */
	const [dropdownOpen, setDropdownOpen] = useState(false);

	/** Ref for the dropdown container — used for click-outside detection */
	const dropdownRef = useRef<HTMLDivElement>(null);

	/* ---------------------------------------------------------------
	 * Global search state
	 * As the user types, we debounce-fetch matching work orders and
	 * inspections in parallel and show them in a dropdown beneath the
	 * input. Clicking a result navigates to that detail page.
	 * --------------------------------------------------------------- */

	/** Current text in the search input (controlled). */
	const [searchQuery, setSearchQuery] = useState('');

	/** Work order matches returned from the most recent search (up to 5). */
	const [searchWorkOrders, setSearchWorkOrders] = useState<WorkOrder[]>([]);

	/** Inspection matches returned from the most recent search (up to 5). */
	const [searchInspections, setSearchInspections] = useState<Inspection[]>([]);

	/** Whether a fetch is in flight — drives the "Searching..." indicator. */
	const [searchLoading, setSearchLoading] = useState(false);

	/** Whether the results panel is visible (focused + query >= 2 chars). */
	const [searchOpen, setSearchOpen] = useState(false);

	/** Wraps input + results panel so the click-outside listener can close us. */
	const searchRef = useRef<HTMLDivElement>(null);

	/** Monotonically-increasing request id — used to ignore stale responses
	 *  when the user types quickly and an earlier request resolves last. */
	const searchRequestIdRef = useRef(0);

	/* ---------------------------------------------------------------
	 * Derived values
	 * --------------------------------------------------------------- */

	/** Full display name built from user profile */
	const displayName = user ? `${user.firstName} ${user.lastName}` : '';

	/** User's role for display in the stacked name/role label */
	const displayRole = user?.role
		? user.role.split('_').map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')
		: '';

	/** First initial for the avatar circle */
	const avatarInitial = displayName ? displayName.charAt(0).toUpperCase() : '?';

	/* ---------------------------------------------------------------
	 * Click-outside handler for the dropdown menu
	 * Closes the dropdown when the user clicks anywhere outside of it
	 * --------------------------------------------------------------- */

	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (
				dropdownRef.current &&
				!dropdownRef.current.contains(event.target as Node)
			) {
				setDropdownOpen(false);
			}
		};

		/* Only add the listener when the dropdown is open to avoid unnecessary
		   event handling when the menu is closed */
		if (dropdownOpen) {
			document.addEventListener('mousedown', handleClickOutside);
		}

		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, [dropdownOpen]);

	/* ---------------------------------------------------------------
	 * Debounced global search effect
	 * Fires whenever the query changes; waits 250ms before hitting the
	 * API so quick typing doesn't spam the backend. Only searches when
	 * the trimmed query has at least 2 characters. Each request is
	 * tagged with an id so out-of-order responses can be discarded.
	 * --------------------------------------------------------------- */

	useEffect(() => {
		const trimmed = searchQuery.trim();

		/* Clear results when the user empties the input — but keep the
		   panel hidden rather than showing a stale "no results" state. */
		if (trimmed.length < 2) {
			setSearchWorkOrders([]);
			setSearchInspections([]);
			setSearchLoading(false);
			return;
		}

		/* Tag this request so a slow earlier response can't overwrite a
		   later one. We compare against the latest id on resolve. */
		const requestId = ++searchRequestIdRef.current;

		/* Debounce: only issue the fetch if the user stops typing for
		   250ms. The cleanup below clears the timeout on every keystroke. */
		const timeoutId = setTimeout(async () => {
			setSearchLoading(true);
			try {
				/* Fire both list endpoints in parallel and cap each side
				   at 5 results so the dropdown stays compact. */
				const [woRes, inspRes] = await Promise.all([
					workOrderService.getAll({ search: trimmed, per_page: '5' }),
					inspectionService.getAll({ search: trimmed, per_page: '5' }),
				]);

				/* Ignore the response if a newer search has already started. */
				if (requestId !== searchRequestIdRef.current) return;

				/* api.get wraps the payload as `{ data, status, ok }`, so the
				   paginated list lives at `res.data.data`. */
				setSearchWorkOrders(woRes.data?.data ?? []);
				setSearchInspections(inspRes.data?.data ?? []);
			} catch {
				/* On error, blank the lists — the empty state in the panel
				   communicates "no matches" rather than surfacing a toast for
				   what is essentially a passive lookup. */
				if (requestId === searchRequestIdRef.current) {
					setSearchWorkOrders([]);
					setSearchInspections([]);
				}
			} finally {
				if (requestId === searchRequestIdRef.current) {
					setSearchLoading(false);
				}
			}
		}, 250);

		return () => clearTimeout(timeoutId);
	}, [searchQuery]);

	/* ---------------------------------------------------------------
	 * Click-outside handler for the global search dropdown
	 * Closes the results panel when the user clicks anywhere outside
	 * the search input + dropdown container.
	 * --------------------------------------------------------------- */

	useEffect(() => {
		if (!searchOpen) return;

		const handleClickOutside = (event: MouseEvent) => {
			if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
				setSearchOpen(false);
			}
		};

		document.addEventListener('mousedown', handleClickOutside);
		return () => document.removeEventListener('mousedown', handleClickOutside);
	}, [searchOpen]);

	/* ---------------------------------------------------------------
	 * Event handlers
	 * --------------------------------------------------------------- */

	/**
	 * Navigates to a search result's detail page, clears the input,
	 * and closes the dropdown. Used by both work order and inspection
	 * result rows.
	 */
	const handleSearchResultClick = (path: string) => {
		setSearchOpen(false);
		setSearchQuery('');
		navigate(path);
	};

	/**
	 * Navigates to a route via the dropdown menu, then closes the dropdown.
	 */
	const handleDropdownNavigate = (href: string) => {
		setDropdownOpen(false);
		navigate(href);
	};

	/**
	 * Handles sign-out: closes the dropdown, then calls the auth logout.
	 */
	const handleLogout = () => {
		setDropdownOpen(false);
		logout();
	};

	/* ---------------------------------------------------------------
	 * Shared sub-components (inline) — Sync icon and Notification bell
	 * --------------------------------------------------------------- */

	/**
	 * Renders the sync status icon with a small colored dot.
	 * Green dot = online, red dot = offline.
	 */
	const renderSyncIcon = () => (
		<div className="relative flex items-center justify-center" aria-label={isOnline ? 'Online' : 'Offline'}>
			{/* Rotating arrows icon — represents synchronization */}
			<svg className="w-20px h-20px text-brand-text-muted" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
				<path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182M2.985 19.644l3.181-3.183" />
			</svg>
			{/* Connectivity dot — positioned at the bottom-right of the icon */}
			<span
				className={[
					'absolute -bottom-1px -right-1px w-8px h-8px rounded-full border-2px border-white',
					isOnline ? 'bg-status-success' : 'bg-status-error',
				].join(' ')}
			/>
		</div>
	);

	/**
	 * Renders the notification bell icon with an optional red unread dot.
	 * The dot is currently hardcoded as hidden — wire up to a notification
	 * count context or prop when notification support is added.
	 */
	const renderNotificationBell = () => (
		<button
			type="button"
			className="relative flex items-center justify-center w-44px h-44px rounded-full text-brand-text-muted hover:bg-brand-surface hover:text-brand-primary transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary"
			aria-label="Notifications"
		>
			{/* Bell icon */}
			<svg className="w-20px h-20px" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
				<path strokeLinecap="round" strokeLinejoin="round" d="M14.857 17.082a23.848 23.848 0 0 0 5.454-1.31A8.967 8.967 0 0 1 18 9.75V9A6 6 0 0 0 6 9v.75a8.967 8.967 0 0 1-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 0 1-5.714 0m5.714 0a3 3 0 1 1-5.714 0" />
			</svg>
			{/* Unread notification dot — hidden by default, shown when there are
				unread notifications. Toggle visibility by changing 'hidden' to '' */}
			<span className="hidden absolute top-6px right-6px w-8px h-8px rounded-full bg-status-error border-2px border-white" />
		</button>
	);

	/* ---------------------------------------------------------------
	 * Component JSX
	 * --------------------------------------------------------------- */

	return (
		<header
			data-ref="organism-top-bar"
			className={[
				/* BEM block marker (structural reference only) */
				'top-bar',
				/* Sticky positioning so the bar stays visible while scrolling */
				'sticky top-0',
				/* White background with bottom border and subtle shadow */
				'bg-white border-b border-brand-border shadow-sm',
				/* Fixed 64px height with centered content */
				'h-64px',
				/* High z-index to sit above page content but below modals */
				'z-30',
				/* Forwarded className last so callers can extend/override */
				className ?? '',
			].join(' ')}
		>
			{/* Inner container: horizontal flex row with padding */}
			<div className="top-bar__inner flex items-center h-full px-12px tablet:px-24px">

				{/* ===== Mobile-only: Brand text ===== */}
				{/* Shows "GBRANDS" on mobile where the Sidebar is hidden */}
				<a
					href="/"
					onClick={(e) => { e.preventDefault(); navigate('/'); }}
					/* inline-flex + min-h-44px: the wordmark itself is only ~24px tall,
					   and on mobile this is the only way back to the dashboard from
					   the top bar. */
					/* min-w-0 + overflow-hidden so the wordmark is what gives way when the
					   row runs out of room. Flex items default to min-width:auto,
					   which is why a long brand used to push the controls off-screen
					   instead of clipping itself — and the brand is white-labelable
					   (CONVENTIONS.md §4), so its width is not ours to assume. */
					className="top-bar__brand tablet:hidden inline-flex items-center min-w-0 overflow-hidden min-h-44px hover:opacity-80 transition-opacity"
				>
					<BrandWordmark size="sm" />
				</a>

				{/* ===== Desktop-only: Search input + results dropdown ===== */}
				{/* Rounded search field with a magnifying glass icon and placeholder text.
					Hidden below `desktop` — search has a home on the dashboard, and on
					an iPad the sidebar (219px) plus the action cluster left this field
					about 70px wide, rendering as a pill reading "Se".
					Typing >=2 chars opens a dropdown listing matching work orders and
					inspections; clicking a row navigates to that detail page. */}
				<div className="top-bar__search hidden desktop:flex items-center flex-1 min-w-[220px] max-w-[400px]">
					<div className="relative w-full" ref={searchRef}>
						{/* Search (magnifying glass) icon inside the input */}
						<svg
							className="absolute left-12px top-1/2 -translate-y-1/2 w-16px h-16px text-brand-text-subtle pointer-events-none"
							fill="none"
							viewBox="0 0 24 24"
							strokeWidth="2"
							stroke="currentColor"
						>
							<path strokeLinecap="round" strokeLinejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z" />
						</svg>
						{/* Search input field — controlled. Focus + non-empty query
							opens the dropdown; Escape closes it without losing the
							typed text. */}
						<input
							type="text"
							role="combobox"
							value={searchQuery}
							onChange={(e) => { setSearchQuery(e.target.value); setSearchOpen(true); }}
							onFocus={() => { if (searchQuery.trim().length >= 2) setSearchOpen(true); }}
							onKeyDown={(e) => { if (e.key === 'Escape') setSearchOpen(false); }}
							placeholder="Search orders or inspections..."
							aria-label="Search work orders and inspections"
							aria-expanded={searchOpen}
							aria-controls="topbar-search-results"
							aria-autocomplete="list"
							className={[
								/* Layout and sizing */
								'w-full pl-36px pr-16px py-8px',
								/* Rounded appearance with gray background */
								'rounded-full bg-brand-surface',
								/* Text styling */
								'text-16px desktop:text-14px font-sans text-brand-text placeholder:text-brand-primary placeholder:opacity-90 placeholder:font-bold',
								/* Orange border always visible, brighter on focus */
								'border-2 border-brand-primary focus:border-brand-primary',
								/* Remove default outline, add ring on focus */
								'outline-none focus:ring-2 focus:ring-brand-primary/20',
								/* Smooth transition for focus states */
								'transition-all duration-200',
							].join(' ')}
						/>

						{/* ===== Search results dropdown =====
							Renders only when the input is focused (searchOpen) AND
							the user has typed at least 2 characters. Shows two
							sections — Work Orders and Inspections — with up to 5
							rows each. Loading and empty states share the same
							panel so the layout doesn't jump. */}
						{searchOpen && searchQuery.trim().length >= 2 && (
							<div
								id="topbar-search-results"
								role="listbox"
								className={[
									/* Positioning: directly below the input, full-width */
									'absolute left-0 right-0 top-full mt-8px',
									/* Sizing — scroll long result lists rather than expand */
									'max-h-[60vh] overflow-y-auto',
									/* Appearance: white card with brand border + shadow */
									'bg-white rounded-12px shadow-lg ring-1 ring-brand-border',
									/* Padding inside the panel */
									'py-8px',
									/* Stay above sibling page content */
									'z-50',
								].join(' ')}
							>
								{/* Loading row — shown while a fetch is in flight and
									there are no cached results to display yet. */}
								{searchLoading && searchWorkOrders.length === 0 && searchInspections.length === 0 && (
									<div className="px-16px py-12px text-14px font-sans text-brand-text-muted">
										Searching...
									</div>
								)}

								{/* Empty state — fetch finished but neither list has
									anything to show. */}
								{!searchLoading && searchWorkOrders.length === 0 && searchInspections.length === 0 && (
									<div className="px-16px py-12px text-14px font-sans text-brand-text-muted">
										No matches found.
									</div>
								)}

								{/* ----- Work Orders section ----- */}
								{searchWorkOrders.length > 0 && (
									<div>
										{/* Section header — small, uppercase, brand muted */}
										<div className="px-16px pt-4px pb-4px text-11px font-semibold uppercase tracking-wider text-brand-text-muted">
											Work Orders
										</div>
										{searchWorkOrders.map((wo) => (
											<button
												key={`wo-${wo.id}`}
												type="button"
												role="option"
												aria-selected={false}
												onClick={() => handleSearchResultClick(`/work-orders/${wo.id}`)}
												className="flex flex-col justify-center w-full px-16px py-8px min-h-44px text-left hover:bg-brand-surface transition-colors"
											>
												{/* Primary line: the project name (or a fallback). */}
												<div className="text-14px font-sans font-semibold text-brand-text truncate">
													{wo.projectName || 'Untitled work order'}
												</div>
												{/* Secondary line: company • location for quick disambiguation. */}
												<div className="text-12px font-sans text-brand-text-muted truncate">
													{[wo.companyName, wo.locationName].filter(Boolean).join(' • ') || '—'}
												</div>
											</button>
										))}
									</div>
								)}

								{/* Divider between the two sections when both have results. */}
								{searchWorkOrders.length > 0 && searchInspections.length > 0 && (
									<div className="border-t border-brand-border my-4px" />
								)}

								{/* ----- Inspections section ----- */}
								{searchInspections.length > 0 && (
									<div>
										<div className="px-16px pt-4px pb-4px text-11px font-semibold uppercase tracking-wider text-brand-text-muted">
											Inspections
										</div>
										{searchInspections.map((insp) => (
											<button
												key={`insp-${insp.id}`}
												type="button"
												role="option"
												aria-selected={false}
												onClick={() => handleSearchResultClick(`/inspections/${insp.id}`)}
												className="flex flex-col justify-center w-full px-16px py-8px min-h-44px text-left hover:bg-brand-surface transition-colors"
											>
												{/* Primary line: building name with a date fallback. */}
												<div className="text-14px font-sans font-semibold text-brand-text truncate">
													{insp.buildingName || 'Inspection'}
												</div>
												{/* Secondary line: company • contact for context. */}
												<div className="text-12px font-sans text-brand-text-muted truncate">
													{[insp.companyName, insp.contactName].filter(Boolean).join(' • ') || '—'}
												</div>
											</button>
										))}
									</div>
								)}
							</div>
						)}
					</div>
				</div>

				{/* ===== Flexible spacer ===== */}
				{/* Pushes the right-side icons to the far right */}
				<div className="flex-1" />

				{/* ===== Right-side action icons ===== */}
				{/* Icons are shown in a horizontal row with consistent gap spacing */}
				<div className="top-bar__actions flex shrink-0 items-center gap-8px tablet:gap-16px">

					{/* --- Quick "Add User" button (managers and up) --- */}
					{/* Lets privileged users onboard a tech/individual from anywhere.
						Icon-only on mobile, icon + label on desktop. */}
					{canAddUsers && (
						<button
							type="button"
							onClick={() => setAddUserOpen(true)}
							className="inline-flex items-center gap-6px rounded-full px-10px py-6px tablet:px-12px min-h-44px desktop:min-h-36px text-14px font-sans font-medium text-brand-primary border border-brand-primary hover:bg-brand-primary/10 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary"
							aria-label="Add new user"
						>
							{/* Plus / user icon */}
							<svg className="w-18px h-18px" fill="none" viewBox="0 0 24 24" strokeWidth="1.8" stroke="currentColor">
								<path strokeLinecap="round" strokeLinejoin="round" d="M19 7.5v6m3-3h-6m-1.5-2.25a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0ZM3 19.235v-.11a6.375 6.375 0 0 1 12.75 0v.109A12.318 12.318 0 0 1 9.374 21c-2.331 0-4.512-.645-6.374-1.766Z" />
							</svg>
							<span className="hidden tablet:inline">Add User</span>
						</button>
					)}

					{/* --- Sync status icon --- */}
					{/* Shows rotating arrows with a green/red connectivity dot */}
					{renderSyncIcon()}

					{/* --- Notification bell --- */}
					{/* Bell icon with optional red unread dot */}
					{renderNotificationBell()}

					{/* --- User avatar and dropdown (desktop shows name + role) --- */}
					<div className="relative" ref={dropdownRef}>
						{/* Avatar trigger button */}
						<button
							type="button"
							onClick={() => setDropdownOpen(!dropdownOpen)}
							className={[
								'top-bar__avatar-button',
								/* On mobile the button is just the 36px avatar circle —
								   min-h/w-44px pads the hit area out to the touch floor
								   without enlarging the avatar itself. */
								'flex items-center justify-center gap-8px rounded-full min-h-44px min-w-44px tablet:min-w-0',
								'text-14px font-sans text-brand-text-muted',
								'focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary focus-visible:ring-offset-2',
								'transition-colors duration-200',
							].join(' ')}
							aria-expanded={dropdownOpen}
							aria-haspopup="true"
						>
							{/* Desktop-only: User name and role stacked vertically */}
							<div className="hidden tablet:flex flex-col items-end mr-4px">
								{/* User's full name */}
								<span className="text-14px font-semibold text-brand-text leading-tight">
									{displayName}
								</span>
								{/* User's role — capitalized */}
								<span className="text-11px text-brand-text-muted leading-tight">
									{displayRole}
								</span>
							</div>
							{/* Avatar circle — uses 50x50 thumb if available, falls back to initial */}
							{user?.profilePhotoThumbUrl ? (
								<img src={user.profilePhotoThumbUrl} alt="" className="w-36px h-36px rounded-full object-cover" />
							) : (
								<div className="flex items-center justify-center w-36px h-36px rounded-full bg-brand-primary text-white text-14px font-semibold font-sans">
									{avatarInitial}
								</div>
							)}
						</button>

						{/* --- Avatar dropdown menu --- */}
						{/* Rendered conditionally when the avatar is clicked.
							Positioned absolutely below the trigger button. */}
						{dropdownOpen && (
							<div
								className={[
									/* Positioning: below the trigger, aligned to right edge */
									'absolute right-0 mt-8px',
									/* Sizing and appearance */
									'w-192px rounded-8px bg-white py-4px',
									/* Shadow and border for elevation */
									'shadow-lg ring-1 ring-brand-border',
									/* Animate from the top-right origin */
									'origin-top-right',
									/* Ensure dropdown sits above other content */
									'z-50',
								].join(' ')}
							>
								{/* My Profile link — navigates to /profile */}
								<button
									type="button"
									onClick={() => handleDropdownNavigate('/profile')}
									className="flex items-center w-full px-16px py-8px min-h-44px text-left text-14px font-sans text-brand-text-muted hover:bg-brand-surface hover:text-brand-primary transition-colors"
								>
									My Profile
								</button>

								{/* Divider between profile and sign-out */}
								<div className="border-t border-brand-border my-4px" />

								{/* Sign out button — calls logout and closes dropdown */}
								<button
									type="button"
									onClick={handleLogout}
									className="flex items-center w-full px-16px py-8px min-h-44px text-left text-14px font-sans text-brand-text-muted hover:bg-brand-surface hover:text-brand-primary transition-colors"
								>
									Sign out
								</button>
							</div>
						)}
					</div>
				</div>
			</div>

			{/* Quick "Add New User" popup — only mounted for privileged users. */}
			{canAddUsers && (
				<AddUserModal isOpen={addUserOpen} onClose={() => setAddUserOpen(false)} />
			)}
		</header>
	);
};

export default TopBar;
